import type { JSX } from 'preact';
import { useCallback, useRef, useState } from 'preact/hooks';
import type { Signal } from '@preact/signals';
import {
  type BubblePlacement,
  type PositionSlot,
  writePlacement,
} from '../services/widget-position-store';
import { draggedBubblePos } from '../store/widget-store';
import {
  VIEWPORT_MARGIN,
  clampToViewport,
  computeAnchoredPlacement,
} from './use-bubble-drag';

/**
 * 打开后「框体」(网页面板普通/最大化态、数字人桌面弹窗)的拖动 hook。
 *
 * 与迷你气泡的 use-bubble-drag 区分:气泡是「长按 450ms 进可拖 + 吞 click=打开」;
 * 框体已打开、无「点击打开」语义,应「抓 header 把手立即拖」。两者交互模型不同,
 * 故不复用 useBubbleDrag,只复用其纯函数(clampToViewport / computeAnchoredPlacement)。
 *
 * 交互:把手(header)上 pointerdown,若落点命中按钮/输入/菜单等交互控件(hitInteractive)
 * 则不发起拖动(让按钮照常工作);否则 setPointerCapture 进入拖动,pointermove 跟手并按
 * 框体实测尺寸 clamp 在视口内;落手算就近角锚定 placement → 写各自信号 + writePlacement
 * (24h 信封),并吞掉随后冒泡的 click(发生过位移时)。
 *
 * 定位:拖动中返回 dragStyle(left/top + transform:none,数字人弹窗另加 position:fixed);
 * 非拖动态返回 null,由组件回落到「持久化落点(placementToStyle)/CSS 默认」。
 */

/** 把手内命中这些则不发起拖动(按钮/链接/表单控件/显式标记) */
const NO_DRAG_TAGS = new Set(['BUTTON', 'A', 'INPUT', 'SELECT', 'TEXTAREA', 'LABEL']);
const NO_DRAG_ROLES = new Set(['button', 'listbox', 'option', 'menuitem']);

/**
 * pointerdown 落点是否在「不可拖」的交互元素上。
 * Shadow DOM 下事件 target 不被 retarget(监听器就在 shadow 内的 header 上),但仍统一走
 * composedPath 取穿过边界的真实路径(仓库硬约定);只检查 handle(currentTarget)与 target 之间的后代。
 */
function hitInteractive(e: PointerEvent): boolean {
  const handle = e.currentTarget as EventTarget | null;
  const path = typeof e.composedPath === 'function' ? e.composedPath() : [];
  const nodes = path.length ? path : [e.target];
  for (const n of nodes) {
    if (n === handle) break; // 到达把手自身即止(把手及其祖先不算交互区)
    if (!(n instanceof HTMLElement)) continue;
    if (NO_DRAG_TAGS.has(n.tagName)) return true;
    const role = n.getAttribute('role');
    if (role && NO_DRAG_ROLES.has(role)) return true;
    if (n.hasAttribute('data-dcs-nodrag')) return true;
  }
  return false;
}

export interface FrameDragOptions {
  /** 是否启用(移动端/整页 H5 下为 false:不发起拖动、不记位置) */
  enabled: boolean;
  /** 落手写入的位置信号(各面各自) */
  posSignal: Signal<BubblePlacement | null>;
  /** 持久化槽位 */
  slot: PositionSlot;
  /** 客户端隔离 id(直传 widgetClientId.value) */
  clientId: string | null;
  /** clamp 兜底尺寸(实测 rect 优先;拿不到时用此标称值) */
  fallbackW: number;
  fallbackH: number;
  /**
   * 拖动中/落点定位是否需要 position:fixed。
   * 数字人弹窗 .dcs-modal 默认 position:relative(在 .dcs-overlay flex 居中里),
   * 需 fixed 才能脱离居中流按视口坐标定位;网页面板本就 fixed,传 false。
   */
  fixed?: boolean;
  /**
   * 落手后是否把最小化气泡同步挪到框体所在角(默认 true)。
   * 让「拖动框体 → 收起后气泡就在框体附近」,而非跳回旧角;气泡 web/digital 共用
   * draggedBubblePos + 'bubble' 槽,比框体小,套用同一角锚定 x/y 必在视口内。
   */
  syncBubble?: boolean;
}

/** 绑到把手(header)的 pointer / click 事件集合(供数字人 pane 经 props 透传) */
export interface FrameDragHandleProps {
  onPointerDown: (e: PointerEvent) => void;
  onPointerMove: (e: PointerEvent) => void;
  onPointerUp: (e: PointerEvent) => void;
  onPointerCancel: (e: PointerEvent) => void;
  onClick: (e: MouseEvent) => void;
}

export interface FrameDragResult {
  /** 是否正在拖动(可用于加 .is-dragging 类切 grabbing 光标) */
  dragging: boolean;
  /** 拖动中的实时 inline style;非拖动态为 null(组件回落持久化落点 / CSS 默认) */
  dragStyle: JSX.CSSProperties | null;
  /** 绑到把手(header)的 pointer / click 事件 */
  handleProps: FrameDragHandleProps;
}

export function useFrameDrag(opts: FrameDragOptions): FrameDragResult {
  const { enabled, posSignal, slot, clientId, fallbackW, fallbackH, fixed, syncBubble } = opts;

  const [dragging, setDragging] = useState(false);
  const [live, setLive] = useState<{ left: number; top: number } | null>(null);

  const active = useRef(false);
  const didMove = useRef(false);
  const justDragged = useRef(false);
  const grabDX = useRef(0);
  const grabDY = useRef(0);
  const frameEl = useRef<HTMLElement | null>(null);

  const onPointerDown = useCallback(
    (e: PointerEvent) => {
      if (!enabled || e.button !== 0) return; // 仅主键 / 触摸 / 笔
      if (hitInteractive(e)) return; // 落在按钮/输入/菜单上:不拖,让其照常工作
      const handle = e.currentTarget as HTMLElement;
      const frame = handle.closest('.dcs-web-panel, .dcs-modal') as HTMLElement | null;
      if (!frame) return;
      const rect = frame.getBoundingClientRect();
      frameEl.current = frame;
      grabDX.current = e.clientX - rect.left;
      grabDY.current = e.clientY - rect.top;
      active.current = true;
      didMove.current = false;
      try {
        handle.setPointerCapture(e.pointerId);
      } catch {
        /* jsdom / 不支持 PointerCapture 时忽略 */
      }
      setDragging(true);
      // 以当前 rect 起步:relative→fixed 切换(数字人)时坐标对齐,无跳变
      setLive({ left: rect.left, top: rect.top });
    },
    [enabled],
  );

  const onPointerMove = useCallback(
    (e: PointerEvent) => {
      if (!active.current || !frameEl.current) return;
      e.preventDefault();
      didMove.current = true;
      const rect = frameEl.current.getBoundingClientRect();
      const { left, top } = clampToViewport(
        e.clientX - grabDX.current,
        e.clientY - grabDY.current,
        rect.width || fallbackW,
        rect.height || fallbackH,
        window.innerWidth,
        window.innerHeight,
        VIEWPORT_MARGIN,
      );
      setLive({ left, top });
    },
    [fallbackW, fallbackH],
  );

  const onPointerUp = useCallback(
    (e: PointerEvent) => {
      const handle = e.currentTarget as HTMLElement;
      try {
        handle.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      if (active.current && didMove.current && frameEl.current) {
        const rect = frameEl.current.getBoundingClientRect();
        const placement = computeAnchoredPlacement(
          { left: rect.left, top: rect.top, width: rect.width, height: rect.height },
          window.innerWidth,
          window.innerHeight,
        );
        const now = Date.now();
        posSignal.value = placement;
        writePlacement(clientId, slot, placement, now);
        // 框体被拖动后,最小化气泡同步挪到框体所在角:收起时气泡就在框体附近,不再跳回旧角。
        if (syncBubble !== false) {
          draggedBubblePos.value = placement;
          writePlacement(clientId, 'bubble', placement, now);
        }
        justDragged.current = true; // 吞掉随后冒泡的 click
      }
      active.current = false;
      didMove.current = false;
      frameEl.current = null;
      setDragging(false);
      setLive(null);
    },
    [posSignal, slot, clientId, syncBubble],
  );

  const onPointerCancel = useCallback((e: PointerEvent) => {
    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    active.current = false;
    didMove.current = false;
    frameEl.current = null;
    setDragging(false);
    setLive(null);
  }, []);

  const onClick = useCallback((e: MouseEvent) => {
    if (justDragged.current) {
      justDragged.current = false;
      e.preventDefault();
      e.stopPropagation();
    }
  }, []);

  const dragStyle: JSX.CSSProperties | null =
    dragging && live
      ? {
          ...(fixed ? { position: 'fixed' as const } : null),
          left: `${live.left}px`,
          top: `${live.top}px`,
          right: 'auto',
          bottom: 'auto',
          transform: 'none',
        }
      : null;

  return {
    dragging,
    dragStyle,
    handleProps: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel, onClick },
  };
}
